/**
 * Shared invoice-list UI rules: statement batching vs editable state.
 * Mirrors server guards where possible (locking after batch submission).
 */

export const INVOICE_LOCKING_BATCH_STATUSES = new Set([
  'pending_approval',
  'approved',
  'posted',
]);

export interface InvoiceBatchRowFields {
  item_type?: string | null;
  status?: string | null;
  batch_id?: string | null;
  batch_status?: string | null;
  batch_number?: string | null;
  payment_status?: string | null;
  balance_due?: number | string | null;
  grand_total?: number | string | null;
  is_recurring?: boolean | null;
  parent_recurrence_id?: string | null;
  recurrence_pattern?: string | null;
  custom_fields?: Record<string, unknown> | null;
  metadata?: unknown;
}

function parseMetadata(row: InvoiceBatchRowFields): Record<string, unknown> | null {
  let meta = row.metadata;
  if (typeof meta === 'string') {
    try {
      meta = JSON.parse(meta);
    } catch {
      return null;
    }
  }
  if (!meta || typeof meta !== 'object') return null;
  return meta as Record<string, unknown>;
}

/** True once the invoice is in an approval-posted workflow (statement path). Draft/open batches remain editable. */
export function invoiceIsBatchWorkflowLocked(record: InvoiceBatchRowFields): boolean {
  if (record.item_type && record.item_type !== 'invoice') return false;
  const st = String(record.batch_status || '').trim().toLowerCase();
  if (!st || !record.batch_id) return false;
  return INVOICE_LOCKING_BATCH_STATUSES.has(st);
}

export function invoiceUnifiedPreviewBlocked(record: InvoiceBatchRowFields): boolean {
  return !invoiceIsBatchWorkflowLocked(record);
}

/** Statement column: Pending until locked in batch workflow; then Paid/Unpaid from payment. */
export function getInvoiceStatementDisplay(record: InvoiceBatchRowFields): { key: string; label: string } {
  if (record.item_type && record.item_type !== 'invoice') {
    const ps = String(record.payment_status || record.status || '');
    return { key: ps, label: ps.replace(/_/g, ' ') };
  }

  if (!invoiceIsBatchWorkflowLocked(record)) {
    return { key: 'pending_statement', label: 'Pending' };
  }

  const paidish = ['paid', 'partially_paid'].includes(String(record.payment_status || '').toLowerCase());
  const balance = Number(record.balance_due ?? record.grand_total ?? 0);
  const unpaid = Number.isFinite(balance) ? balance > 0.009 : !paidish;

  if (!unpaid || String(record.payment_status || '').toLowerCase() === 'paid') {
    return { key: 'paid', label: 'Paid' };
  }

  return { key: 'unpaid', label: 'Unpaid' };
}

/** Tab filter helper for FinanceDocumentView header tabs. */
export function invoiceMatchesListStatusTab(record: InvoiceBatchRowFields, tab: string): boolean {
  switch (tab) {
    case 'draft':
      return String(record.status || '').toLowerCase() === 'draft';
    case 'void':
      return String(record.status || '').toLowerCase() === 'void';
    case 'awaiting_statement':
      return invoiceAwaitingStatementBatch(record);
    case 'paid':
      return invoiceIsBatchWorkflowLocked(record) && getInvoiceStatementDisplay(record).key === 'paid';
    case 'unpaid':
      return invoiceIsBatchWorkflowLocked(record) && getInvoiceStatementDisplay(record).key === 'unpaid';
    case 'overdue': {
      const locked = invoiceIsBatchWorkflowLocked(record);
      const overdue =
        String(record.payment_status || '').toLowerCase() === 'overdue' ||
        String(record.status || '').toLowerCase() === 'overdue';
      return locked && overdue;
    }
    case 'recurring':
      return invoiceRowIsRecurring(record);
    default:
      return false;
  }
}

export function invoiceAwaitingStatementBatch(record: InvoiceBatchRowFields): boolean {
  const stDoc = String(record.status || '').toLowerCase();
  if (stDoc === 'draft' || stDoc === 'void') return false;
  return !invoiceIsBatchWorkflowLocked(record);
}

export function invoiceRowIsRecurring(record: InvoiceBatchRowFields): boolean {
  if (record.item_type !== 'invoice') return false;
  if (record.is_recurring === true) return true;
  if (record.parent_recurrence_id) return true;
  const rp = record.recurrence_pattern || record.custom_fields?.recurrence_pattern;
  if (typeof rp === 'string' && rp.trim().length > 0) return true;
  const meta = parseMetadata(record);
  if (
    meta &&
    (meta.recurring_schedule_id ||
      meta.source_recurring_schedule_id ||
      meta.recurring === true ||
      meta.generated_from_schedule === true)
  ) {
    return true;
  }
  return false;
}

export function invoiceDisplayBatchNumber(record: InvoiceBatchRowFields & { invoice_batch_number?: string | null }): string | null {
  const enriched = (record as { invoice_batch_number?: string | null }).invoice_batch_number;
  if (typeof enriched === 'string' && enriched.trim().length > 0) return enriched.trim();

  const n = record.batch_number;
  if (typeof n === 'string' && n.trim().length > 0) return n.trim();
  if (n != null && String(n).trim().length > 0) return String(n).trim();
  const meta = parseMetadata(record);
  const mn = meta?.invoice_batch_number;
  return typeof mn === 'string' && mn.trim().length > 0 ? mn.trim() : null;
}
